import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import { usePageContent } from '@/context/ContentContext';
import { formatRSD } from '@/lib/currency';

export default function ProductsGrid() {
  const c = usePageContent('home');
  const { products } = useCatalog();
  const [addedId, setAddedId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { addItem } = useCart();

  const featured = products.slice(0, 8);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleAdd = (product: (typeof featured)[number]) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      oldPrice: product.oldPrice ?? undefined,
      image: product.image,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  return (
    <section id="products" ref={sectionRef} className="w-full bg-background-100 py-16 md:py-24 overflow-hidden">
      <div className="w-full px-4 md:px-6 max-w-[1450px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
            {c.products_heading}
          </h2>
          <p className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-100 ease-out text-base text-foreground-700 max-w-2xl mx-auto">
            {c.products_subtitle}
          </p>
        </div>

        {featured.length === 0 ? (
          <div className="text-center py-12 text-sm text-foreground-600">
            Proizvodi se trenutno učitavaju…
          </div>
        ) : (

        <div className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-200 ease-out">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
            {featured.map((product, index) => (
              <div
                key={product.id}
                className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out group"
                style={{ transitionDelay: `${index * 75}ms` }}
              >
                <div className="bg-white rounded-lg border border-background-200 overflow-hidden hover:border-amber-300 transition-all duration-300 hover:shadow-md">
                  <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-background-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - Kućni brojevi po meri`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    {product.badge && (
                      <div className="absolute top-3 left-3 px-2.5 py-1 bg-amber-600 text-white text-[10px] font-label font-medium rounded-sm uppercase tracking-wider">
                        {product.badge}
                      </div>
                    )}
                  </Link>
                  <div className="p-4">
                    <Link to={`/product/${product.id}`} className="block">
                      <h3 className="font-label text-sm font-medium text-foreground-950 mb-2 line-clamp-1 hover:text-amber-600 transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-3 h-3 flex items-center justify-center">
                          <i className={`${i < product.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-background-400'} text-[10px]`} />
                        </div>
                      ))}
                      {product.reviews > 0 && (
                        <span className="text-[10px] text-foreground-600 ml-1">({product.reviews})</span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-label text-base font-bold text-foreground-950">
                        {formatRSD(product.price)}
                      </span>
                      {product.oldPrice && (
                        <span className="font-label text-xs text-foreground-500 line-through">
                          {formatRSD(product.oldPrice)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAdd(product)}
                      className={`w-full py-2.5 font-label text-sm font-medium rounded-md transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                        addedId === product.id
                          ? 'bg-green-600 text-white'
                          : 'bg-foreground-950 text-white hover:bg-foreground-900'
                      }`}
                    >
                      <i className={addedId === product.id ? 'ri-check-line text-xs' : 'ri-shopping-cart-line text-xs'} />
                      {addedId === product.id ? 'Dodato' : 'Dodaj u korpu'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-3 border border-foreground-700/30 text-foreground-700 font-label text-sm font-medium rounded-full hover:border-amber-600 hover:text-amber-600 transition-colors whitespace-nowrap"
            >
              {c.products_cta}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
        )}
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}
